import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, InputLabel, FormControl,
  Rating, Chip, Divider, IconButton, Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useIntl } from "react-intl";
import { useAuth } from "../../providers/AuthProvider";
import { useNotification } from "../../providers/NotificationProvider";
import DraggablePaper from "../../components/common/DraggablePaper";
import {
  fetchIdeas, createIdea, updateIdea, deleteIdea,
  fetchEvaluations, createEvaluation, updateEvaluation, deleteEvaluation,
} from "../../api/memberApi";

const IDEA_TARGETS = ["None", "BusinessDomain", "Job", "Task", "BusinessProcess"];

export default function IdeaListPage() {
  const intl = useIntl();
  const { companyId, memberId } = useAuth();
  const notify = useNotification();
  const t = (id, vals) => intl.formatMessage({ id }, vals);

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ideaDialogOpen, setIdeaDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [ideaName, setIdeaName] = useState("");
  const [ideaDescription, setIdeaDescription] = useState("");
  const [ideaUrl, setIdeaUrl] = useState("");
  const [ideaTargetType, setIdeaTargetType] = useState("None");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [evalIdeaId, setEvalIdeaId] = useState(null);
  const [evalIdeaOwner, setEvalIdeaOwner] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [evalEditTarget, setEvalEditTarget] = useState(null);
  const [evalName, setEvalName] = useState("");
  const [evalDescription, setEvalDescription] = useState("");
  const [evalScore, setEvalScore] = useState(3);
  const [evalDeleteId, setEvalDeleteId] = useState(null);

  const [expandedIds, setExpandedIds] = useState({});

  const loadIdeas = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const data = await fetchIdeas(companyId);
      setIdeas(Array.isArray(data) ? data : []);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [companyId, notify]);

  useEffect(() => { loadIdeas(); }, [loadIdeas]);

  const openIdeaDialog = (idea = null) => {
    setEditTarget(idea);
    setIdeaName(idea?.ideaName || "");
    setIdeaDescription(idea?.ideaDescription || "");
    setIdeaUrl(idea?.url || "");
    setIdeaTargetType(idea?.ideaTarget || "None");
    setIdeaDialogOpen(true);
  };

  const handleIdeaSave = async () => {
    if (!ideaName.trim()) { notify.error(t("idea.name.required")); return; }
    try {
      if (editTarget) {
        await updateIdea({ ideaId: editTarget.ideaId, companyId, memberId, targetId: companyId, ideaTarget: ideaTargetType, ideaName: ideaName.trim(), ideaDescription: ideaDescription.trim(), url: ideaUrl.trim() });
        notify.success(t("idea.updateSuccess"));
      } else {
        await createIdea({ companyId, memberId, targetId: companyId, ideaTarget: ideaTargetType, ideaName: ideaName.trim(), ideaDescription: ideaDescription.trim(), url: ideaUrl.trim() });
        notify.success(t("idea.createSuccess"));
      }
      setIdeaDialogOpen(false);
      loadIdeas();
    } catch (e) { notify.apiError(e); }
  };

  const handleDelete = async () => {
    try {
      await deleteIdea(deleteTarget.ideaId);
      notify.success(t("idea.deleteSuccess"));
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadIdeas();
    } catch (e) { notify.apiError(e); }
  };

  const openEvalDialog = async (ideaId, ideaMemberId) => {
    setEvalIdeaId(ideaId);
    setEvalIdeaOwner(ideaMemberId);
    setEvalEditTarget(null);
    setEvalName(""); setEvalDescription(""); setEvalScore(3);
    try {
      const data = await fetchEvaluations(ideaId);
      setEvaluations(Array.isArray(data) ? data : []);
    } catch (e) { setEvaluations([]); }
    setEvalDialogOpen(true);
  };

  const openEvalEdit = (ev) => {
    setEvalEditTarget(ev);
    setEvalName(ev.evaluationName || "");
    setEvalDescription(ev.evaluationDescription || "");
    setEvalScore(ev.evaluationNumber || 3);
  };

  const handleEvalSave = async () => {
    if (!evalName.trim() || !evalDescription.trim()) { notify.error(t("idea.evaluation.required")); return; }
    try {
      if (evalEditTarget) {
        await updateEvaluation({ evaluationId: evalEditTarget.evaluationId, companyId, evaluationTarget: "Idea", targetId: evalIdeaId, evaluationName: evalName.trim(), evaluationDescription: evalDescription.trim(), evaluationNumber: evalScore, evaluator: memberId, evaluatorId: memberId });
        notify.success(t("idea.evaluation.updateSuccess"));
      } else {
        await createEvaluation({ companyId, evaluationTarget: "Idea", targetId: evalIdeaId, evaluationName: evalName.trim(), evaluationDescription: evalDescription.trim(), evaluationNumber: evalScore, evaluator: memberId, evaluatorId: memberId });
        notify.success(t("idea.evaluation.createSuccess"));
      }
      setEvalEditTarget(null);
      setEvalName(""); setEvalDescription(""); setEvalScore(3);
      const data = await fetchEvaluations(evalIdeaId);
      setEvaluations(Array.isArray(data) ? data : []);
      loadIdeas();
    } catch (e) { notify.apiError(e); }
  };

  const handleEvalDelete = async () => {
    try {
      await deleteEvaluation(evalDeleteId);
      notify.success(t("idea.evaluation.deleteSuccess"));
      setEvalDeleteId(null);
      const data = await fetchEvaluations(evalIdeaId);
      setEvaluations(Array.isArray(data) ? data : []);
      loadIdeas();
    } catch (e) { notify.apiError(e); }
  };

  const toggleExpand = (ideaId) =>
    setExpandedIds((prev) => ({ ...prev, [ideaId]: !prev[ideaId] }));

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("menu.ideas")}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openIdeaDialog()}>
          {t("idea.add.button")}
        </Button>
      </Box>

      {loading ? (
        <Typography color="text.secondary">{t("common.loading")}</Typography>
      ) : ideas.length === 0 ? (
        <Typography color="text.secondary">{t("idea.empty")}</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {ideas.map((idea) => (
            <Card key={idea.ideaId} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{idea.ideaName}</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                      {idea.ideaTarget && idea.ideaTarget !== "None" && (
                        <Chip label={t(`idea.target.${idea.ideaTarget}`)} size="small" />
                      )}
                      {idea.averageScore > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Rating value={Number(idea.averageScore)} readOnly max={5} size="small" precision={0.1} />
                          <Typography variant="caption" color="text.secondary">({Number(idea.averageScore).toFixed(1)})</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => toggleExpand(idea.ideaId)}>
                    {expandedIds[idea.ideaId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={!!expandedIds[idea.ideaId]}>
                  <Box sx={{ mt: 1 }}>
                    {idea.ideaDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
                        {idea.ideaDescription}
                      </Typography>
                    )}
                    {idea.url && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <a href={idea.url} target="_blank" rel="noopener noreferrer">{idea.url}</a>
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 0.5 }}>
                      {(idea.jaName || idea.fullName) && (
                        <Typography variant="caption" color="text.secondary">{idea.jaName || idea.fullName}</Typography>
                      )}
                      {idea.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {t("common.createdAt")}: {new Date(idea.createdAt).toLocaleString("ja-JP")}
                        </Typography>
                      )}
                      {idea.updatedAt && idea.updatedAt !== idea.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {t("common.updatedAt")}: {new Date(idea.updatedAt).toLocaleString("ja-JP")}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
              <CardActions sx={{ pt: 0, gap: 0.5 }}>
                <Button size="small" startIcon={<StarIcon />} onClick={() => openEvalDialog(idea.ideaId, idea.memberId)}>
                  {t("idea.evaluation.button")}
                </Button>
                <Button size="small" startIcon={<EditIcon />} onClick={() => openIdeaDialog(idea)}>{t("common.edit")}</Button>
                <Button size="small" color="error" startIcon={<DeleteIcon />}
                  onClick={() => { setDeleteTarget(idea); setDeleteDialogOpen(true); }}>
                  {t("common.delete")}
                </Button>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* アイデア追加・編集ダイアログ */}
      <Dialog open={ideaDialogOpen} onClose={() => setIdeaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{editTarget ? t("idea.edit.title") : t("idea.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label={t("idea.name")} required fullWidth value={ideaName} onChange={(e) => setIdeaName(e.target.value)} />
            <TextField label={t("idea.description")} fullWidth multiline rows={3} value={ideaDescription} onChange={(e) => setIdeaDescription(e.target.value)} />
            <TextField label={t("idea.url")} fullWidth value={ideaUrl} onChange={(e) => setIdeaUrl(e.target.value)} />
            <FormControl fullWidth>
              <InputLabel>{t("idea.target")}</InputLabel>
              <Select value={ideaTargetType} label={t("idea.target")} onChange={(e) => setIdeaTargetType(e.target.value)}>
                {IDEA_TARGETS.map((tgt) => (
                  <MenuItem key={tgt} value={tgt}>{t(`idea.target.${tgt}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdeaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleIdeaSave}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("idea.deleteConfirm.title")}</DialogTitle>
        <DialogContent>
          <Typography>{t("idea.deleteConfirm.message", { name: deleteTarget?.ideaName || "" })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      {/* 評価ダイアログ */}
      <Dialog open={evalDialogOpen} onClose={() => setEvalDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("idea.evaluation.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {/* 自己評価ブロック */}
            {String(evalIdeaOwner) === String(memberId) ? (
              <Typography color="text.secondary" variant="body2">
                {t("idea.cannotSelfEvaluate")}
              </Typography>
            ) : (
              <>
                <Typography variant="subtitle2">{evalEditTarget ? t("idea.evaluation.edit") : t("idea.evaluation.add")}</Typography>
                <TextField label={t("idea.evaluation.name")} required fullWidth value={evalName} onChange={(e) => setEvalName(e.target.value)} />
                <TextField label={t("idea.evaluation.description")} required fullWidth multiline rows={2} value={evalDescription} onChange={(e) => setEvalDescription(e.target.value)} />
                <Box>
                  <Typography variant="body2" gutterBottom>{t("idea.evaluation.score")}</Typography>
                  <Rating value={evalScore} onChange={(_, v) => setEvalScore(v || 1)} max={5} />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button variant="contained" size="small" onClick={handleEvalSave} disabled={!evalName.trim() || !evalDescription.trim()}>
                    {evalEditTarget ? t("common.save") : t("idea.evaluation.add")}
                  </Button>
                  {evalEditTarget && (
                    <Button size="small" onClick={() => { setEvalEditTarget(null); setEvalName(""); setEvalDescription(""); setEvalScore(3); }}>
                      {t("common.cancel")}
                    </Button>
                  )}
                </Box>
              </>
            )}
            {evaluations.length > 0 && (
              <>
                <Divider />
                <Typography variant="subtitle2">{t("idea.evaluation.list")}</Typography>
                {evaluations.map((ev) => (
                  <Box key={ev.evaluationId} sx={{ p: 1.5, border: "1px solid #e0e0e0", borderRadius: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{ev.evaluationName}</Typography>
                      <Rating value={Number(ev.evaluationNumber)} readOnly max={5} size="small" />
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{ev.evaluationDescription}</Typography>
                    </Box>
                    {String(ev.evaluator) === String(memberId) && (
                      <Box>
                        <IconButton size="small" onClick={() => openEvalEdit(ev)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => setEvalDeleteId(ev.evaluationId)}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvalDialogOpen(false)}>{t("common.cancel")}</Button>
        </DialogActions>
      </Dialog>

      {/* 評価削除確認 */}
      <Dialog open={!!evalDeleteId} onClose={() => setEvalDeleteId(null)} maxWidth="xs" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("idea.evaluation.deleteConfirm.title")}</DialogTitle>
        <DialogContent><Typography>{t("idea.evaluation.deleteConfirm.message")}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setEvalDeleteId(null)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleEvalDelete}>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
